import { NotificationManager } from '../../utils/notifications.js';
import { MenuItemModal } from '../MenuItemModal.js';
import { AnimationManager } from '../../services/AnimationManager.js';
import { setupResponsiveGrid } from '../../utils/gridColumns.js';

export class SidebarIconsLayout {
  constructor(config, menuData, cartCount = 0, i18n = null) {
    this.config = config;
    this.menuData = menuData;
    this.cartCount = cartCount;
    this.i18n = i18n;
    this.modal = new MenuItemModal(this.i18n);
    this.animationManager = new AnimationManager();

    this.columnsPerRow = this.config?.kioskDisplaySettings?.layoutPreferences?.columnsPerRow || 3;

    this.featuredItems = this.getFeaturedItems();
    if (!this.menuData.items) {
      this.menuData.items = {};
    }
    if (this.featuredItems.length > 0) {
      this.menuData.items.featured = this.featuredItems;
    }

    this.categoriesList = this.buildCategories();
    this.currentCategory = this.categoriesList[0]?.id || (this.menuData.categories?.[0]?.id) || 'featured';
  }

  renderCategoryLabel(name) {
    const words = name.split(' ');

    if (words.length === 1) {
      return `<div class="category-label">${name}</div>`;
    }

    if (words.length === 2) {
      const secondWordLength = words[1].length;
      const fontSize = secondWordLength > 8 ? '9px' : '11px';
      return `
        <div class="category-label category-label-two-line">
          <div class="label-row-1">${words[0]}</div>
          <div class="label-row-2" style="font-size: ${fontSize};">${words[1]}</div>
        </div>
      `;
    }

    const midPoint = Math.ceil(words.length / 2);
    const firstLine = words.slice(0, midPoint).join(' ');
    const secondLine = words.slice(midPoint).join(' ');
    const secondLineLength = secondLine.length;
    const fontSize = secondLineLength > 10 ? '8px' : secondLineLength > 7 ? '9px' : '10px';

    return `
      <div class="category-label category-label-two-line">
        <div class="label-row-1">${firstLine}</div>
        <div class="label-row-2" style="font-size: ${fontSize};">${secondLine}</div>
      </div>
    `;
  }

  getFeaturedLabel() {
    return this.t('menu.featured', 'Featured');
  }

  buildCategories() {
    const base = Array.isArray(this.menuData.categories) ? [...this.menuData.categories] : [];
    if (this.featuredItems.length > 0) {
      const label = this.getFeaturedLabel();
      base.unshift({
        id: 'featured',
        name: label,
        icon: 'fa-star',
        description: label,
        isFeaturedVirtual: true
      });
    }
    return base;
  }

  getFeaturedItems() {
    const collected = new Map();

    if (Array.isArray(this.menuData?.specials)) {
      this.menuData.specials.forEach(item => {
        if (item && item.id) {
          collected.set(item.id, item);
        }
      });
    }

    if (this.menuData?.items) {
      Object.values(this.menuData.items).forEach(list => {
        (list || []).forEach(item => {
          if (item && item.id && this.isFeatured(item)) {
            collected.set(item.id, item);
          }
        });
      });
    }

    return Array.from(collected.values()).sort((a, b) => {
      const orderA = a.displayOrder ?? 0;
      const orderB = b.displayOrder ?? 0;
      if (orderA === orderB) {
        return a.name.localeCompare(b.name);
      }
      return orderA - orderB;
    });
  }

  render() {
    return `
      <div class="main-container sidebar-layout" style="display: flex;">
        <!-- Sidebar -->
        <div class="sidebar">
          ${this.config.branding?.logo ?
            `<div class="sidebar-header">
              <div class="logo-container sidebar-logo">
                <button class="sidebar-logo-btn" id="sidebar-logo-btn" aria-label="Back to welcome screen">
                  <img src="${this.config.branding.logo}" alt="${this.config.name} logo" class="sidebar-logo-img">
                </button>
              </div>
            </div>` :
            ''}


          <div class="category-list">
            ${this.categoriesList.map(category => `
              <div class="category-item ${category.id === this.currentCategory ? 'active' : ''} ${category.isFeaturedVirtual ? 'featured-category' : ''}" data-category="${category.id}">
                <div class="category-icon">
                  <i class="fas ${category.icon || 'fa-utensils'}" style="font-size: 20px;"></i>
                </div>
                <div class="category-info">
                  ${this.renderCategoryLabel(category.name)}
                </div>
              </div>
            `).join('')}
          </div>
        </div>

        <!-- Main Content -->
        <div class="main-content">
          <div class="content-header sidebar-content-header">
            <h2 class="section-title sidebar-section-title">
              ${this.getCurrentCategoryName()}
            </h2>
            <div class="header-actions">
              <div class="table-number-bar" id="table-number-bar">
                <div class="table-number-content">
                  <span class="table-label">Stolik</span>
                  <span class="table-number" id="table-number-display">12</span>
                </div>
              </div>
              <button class="language-btn" id="language-btn">
                <i class="fas fa-globe"></i>
              </button>
              <button class="staff-call-btn" id="staff-call-btn">
                <i class="fas fa-bell"></i>
              </button>
              <button class="cart-btn" id="sidebar-cart-btn">
                <i class="fas fa-receipt"></i>
                <span class="cart-count">${this.cartCount}</span>
              </button>
            </div>
          </div>

          <p class="section-description">
            ${this.getCurrentCategoryDescription()}
          </p>

          <div class="menu-items-grid" id="sidebar-menu-grid">
            ${this.renderMenuItems()}
          </div>
        </div>
      </div>
    `;
  }

  renderMenuItems() {
    const currentItems = this.menuData.items[this.currentCategory] || [];
    const availableItems = currentItems.filter(item => item.isAvailable !== false);
    return availableItems.map((item, index) => {
      const cardClasses = ['menu-item-card'];
      if (this.isFeatured(item)) {
        cardClasses.push('is-featured');
      }

      return `
      <div class="${cardClasses.join(' ')}" data-item="${item.id}" data-index="${index}">
        <div class="item-image-container">
          ${item.image ?
            `<img src="${item.image}" alt="${item.name}" class="item-image">` :
            `<div class="item-image-placeholder">
              <i class="fas fa-utensils"></i>
            </div>`}

          ${item.image ? (item.isImageGenerated ? `
            <div class="ai-generated-watermark">
              <span class="watermark-text">ZDJĘCIE WYGENEROWANE</span>
            </div>
          ` : `
            <div class="preview-image-watermark">
              <span class="watermark-text">ZDJĘCIE POGLĄDOWE</span>
            </div>
          `) : ''}

          ${this.renderBadge(item)}
          ${item.dietary && item.dietary.length > 0 ? `
            <div class="dietary-icons">
              ${item.dietary.map(diet => `
                <div class="dietary-icon ${diet}">
                  <i class="${this.getDietaryIcon(diet)}"></i>
                </div>
              `).join('')}
            </div>
          ` : ''}
        </div>
        <div class="item-content">
          <h3 class="item-name">${item.name}</h3>
          <p class="item-description">${item.description}</p>
          <div class="item-footer">
            <div class="item-price">${item.currency || this.config.settings?.currency || 'PLN'} ${typeof item.price === 'object' ? item.price.amount : (item.price || 0)}</div>
            <button class="add-to-cart" data-item="${item.id}">
              <i class="fas fa-plus"></i>
            </button>
          </div>
        </div>
      </div>
    `;
    }).join('');
  }

  getCurrentCategoryIcon() {
    const currentCategory = this.categoriesList.find(cat => cat.id === this.currentCategory);
    return currentCategory?.icon || 'fas fa-utensils';
  }

  getCurrentCategoryName() {
    const currentCategory = this.categoriesList.find(cat => cat.id === this.currentCategory);
    return currentCategory?.name || this.getFeaturedLabel();
  }

  getCurrentCategoryDescription() {
    const currentCategory = this.categoriesList.find(cat => cat.id === this.currentCategory);
    if (currentCategory?.description && !currentCategory.description.startsWith('Category:')) {
      return currentCategory.description;
    }

    return '';
  }

  t(key, fallback) {
    if (!this.i18n || typeof this.i18n.t !== 'function') {
      return fallback;
    }
    const translation = this.i18n.t(key);
    return translation === key ? fallback : translation;
  }

  isFeatured(item) {
    if (!item) return false;
    const badge = (item.badge || '').toString().toLowerCase();
    if (badge === 'featured' || badge === 'recommended') {
      return true;
    }
    const status = (item.status || '').toString().toLowerCase();
    if (status === 'recommended') {
      return true;
    }
    if (item.isPromotedToday || item.metadata?.isPromotedToday) {
      return true;
    }
    return false;
  }

  renderBadge(item) {
    if (!item) return '';
    const badgeKey = (item.badge || '').toString().toLowerCase();
    const label = item.badgeText || this.t('menu.badge.recommended', 'Recommended');

    if (this.isFeatured(item)) {
      return `
        <div class="badge-corner" aria-label="${label}">
          <span class="badge-star" aria-hidden="true">★</span>
          <span class="badge-label">${label}</span>
        </div>
      `;
    }

    if (badgeKey) {
      return `<span class="badge ${item.badge}">${item.badgeText || item.badge}</span>`;
    }
    return '';
  }

  getDietaryIcon(dietary) {
    const icons = {
      vegetarian: 'fas fa-leaf',
      vegan: 'fas fa-seedling',
      'gluten-free': 'fas fa-wheat',
      spicy: 'fas fa-pepper-hot'
    };
    return icons[dietary] || 'fas fa-circle';
  }

  bindEvents() {
    const menuGrid = document.getElementById('sidebar-menu-grid');
    if (menuGrid) {
      setupResponsiveGrid(menuGrid, this.columnsPerRow);
    }

    const logoBtn = document.getElementById('sidebar-logo-btn');
    if (logoBtn) {
      logoBtn.addEventListener('click', () => {
        this.handleLogoClick();
      });
    }

    const categoryItems = document.querySelectorAll('.sidebar-layout .category-item');
    categoryItems.forEach(item => {
      item.addEventListener('click', () => {
        const categoryId = item.getAttribute('data-category');
        this.showCategory(categoryId);
      });
    });

    const toggleBtn = document.querySelector('.sidebar-layout .toggle-sidebar');
    if (toggleBtn) {
      toggleBtn.addEventListener('click', () => {
        NotificationManager.info('Sidebar toggle functionality coming soon!');
      });
    }

    const languageBtn = document.getElementById('language-btn');
    if (languageBtn) {
      languageBtn.addEventListener('click', () => {
        const event = new CustomEvent('showLanguageSelector');
        document.dispatchEvent(event);
      });
    }

    const staffCallBtn = document.getElementById('staff-call-btn');
    if (staffCallBtn) {
      staffCallBtn.addEventListener('click', () => {
        const event = new CustomEvent('showStaffCallModal');
        document.dispatchEvent(event);
      });
    }

    const cartBtn = document.getElementById('sidebar-cart-btn');
    if (cartBtn) {
      cartBtn.addEventListener('click', () => {
        const event = new CustomEvent('showOrderConfirmation');
        document.dispatchEvent(event);
      });
    }

    this.bindItemClickEvents();

    const addToCartBtns = document.querySelectorAll('.sidebar-layout .add-to-cart');
    addToCartBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const itemId = btn.getAttribute('data-item');
        this.handleAddToCart(itemId, btn);
      });
    });

    this.bindProductNavigation();
  }

  bindProductNavigation() {
    const productNavBtns = document.querySelectorAll('.sidebar-layout .image-nav-btn');
    productNavBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const direction = btn.getAttribute('data-direction');
        const currentIndex = parseInt(btn.getAttribute('data-index'));
        this.switchProduct(currentIndex, direction);
      });
    });
  }

  switchProduct(currentIndex, direction) {
    const currentItems = this.menuData.items[this.currentCategory] || [];
    const totalItems = currentItems.length;

    let newIndex;
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % totalItems;
    } else {
      newIndex = currentIndex === 0 ? totalItems - 1 : currentIndex - 1;
    }

    const newItem = currentItems[newIndex];
    const currentCard = document.querySelector(`.sidebar-layout .menu-item-card[data-index="${currentIndex}"]`);

    if (currentCard && newItem) {
      this.updateProductCard(currentCard, newItem, newIndex, totalItems);
    }
  }

  updateProductCard(card, item, index, totalItems) {
    const imgContainer = card.querySelector('.item-image-container');
    const img = imgContainer.querySelector('.item-image');
    if (img) {
      img.src = item.image || '';
      img.alt = item.name;
    }

    card.setAttribute('data-item', item.id);
    card.setAttribute('data-index', index);

    const nameEl = card.querySelector('.item-name');
    if (nameEl) nameEl.textContent = item.name;

    const descEl = card.querySelector('.item-description');
    if (descEl) descEl.textContent = item.description;

    const priceEl = card.querySelector('.item-price');
    if (priceEl) {
      const currency = item.currency || this.config.settings?.currency || 'PLN';
      const price = typeof item.price === 'object' ? item.price.amount : (item.price || 0);
      priceEl.textContent = `${currency} ${price}`;
    }

    const indicator = card.querySelector('.image-indicator');
    if (indicator) {
      indicator.textContent = `${index + 1} of ${totalItems}`;
    }

    const prevBtn = card.querySelector('.prev-product');
    const nextBtn = card.querySelector('.next-product');
    if (prevBtn) prevBtn.setAttribute('data-index', index);
    if (nextBtn) nextBtn.setAttribute('data-index', index);

    const addToCartBtn = card.querySelector('.add-to-cart');
    if (addToCartBtn) {
      addToCartBtn.setAttribute('data-item', item.id);
    }
  }

  bindItemClickEvents() {
    const menuItemCards = document.querySelectorAll('.sidebar-layout .menu-item-card');
    menuItemCards.forEach(card => {
      card.addEventListener('click', (e) => {
        if (e.target.closest('.add-to-cart') || e.target.closest('.image-nav-btn')) {
          return;
        }

        const itemId = card.getAttribute('data-item');
        const item = this.findItemById(itemId);
        if (item) {
          const categoryItems = this.menuData.items[this.currentCategory] || [];
          const availableCategoryItems = categoryItems.filter(item => item.isAvailable !== false);
          this.modal.show(item, availableCategoryItems, this.config);
        }
      });
    });
  }

  async showCategory(categoryId) {
    if (this.currentCategory === categoryId) return;

    const oldCategory = this.currentCategory;
    this.currentCategory = categoryId;

    const categoryItems = document.querySelectorAll('.sidebar-layout .category-item');
    categoryItems.forEach(item => {
      if (item.getAttribute('data-category') === categoryId) {
        item.classList.add('active');
        this.animationManager.animateCategorySelection(item);
      } else {
        item.classList.remove('active');
      }
    });

    const mainContent = document.querySelector('.sidebar-layout .main-content');
    if (mainContent) {
      mainContent.scrollTop = 0;
      const sectionTitle = mainContent.querySelector('.section-title');
      if (sectionTitle) {
        this.animationManager.animateSectionTitle(sectionTitle, this.getCurrentCategoryName());
      }

      const sectionDescription = mainContent.querySelector('.section-description');
      if (sectionDescription) {
        sectionDescription.style.opacity = '0';
        setTimeout(() => {
          sectionDescription.textContent = this.getCurrentCategoryDescription();
          sectionDescription.style.opacity = '1';
        }, 100);
      }

      const menuItemsGrid = mainContent.querySelector('.menu-items-grid');
      if (menuItemsGrid) {
        setupResponsiveGrid(menuItemsGrid, this.columnsPerRow);
        menuItemsGrid.style.opacity = '0';
        menuItemsGrid.style.transition = 'opacity 0.3s ease-in-out';

        menuItemsGrid.innerHTML = this.renderMenuItems();

        setTimeout(() => {
          menuItemsGrid.style.opacity = '1';
          this.animationManager.animateMenuItems(menuItemsGrid, 30);

          this.rebindItemEvents(menuItemsGrid);
        }, 50);
      }

      this.animationManager.smoothScrollToTop(mainContent);
    }
  }

  rebindItemEvents(menuItemsGrid) {
    const newItemCards = menuItemsGrid.querySelectorAll('.menu-item-card');
    newItemCards.forEach(card => {
      card.addEventListener('click', (e) => {
        if (e.target.closest('.add-to-cart') || e.target.closest('.image-nav-btn')) {
          return;
        }
        const itemId = card.getAttribute('data-item');
        const item = this.findItemById(itemId);
        if (item) {
          const categoryItems = this.menuData.items[this.currentCategory] || [];
          const availableCategoryItems = categoryItems.filter(item => item.isAvailable !== false);
          this.modal.show(item, availableCategoryItems, this.config);
        }
      });
    });

    const addToCartBtns = menuItemsGrid.querySelectorAll('.add-to-cart');
    addToCartBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const itemId = btn.getAttribute('data-item');
        this.handleAddToCart(itemId, btn);
      });
    });

    const productNavBtns = menuItemsGrid.querySelectorAll('.image-nav-btn');
    productNavBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const direction = btn.getAttribute('data-direction');
        const currentIndex = parseInt(btn.getAttribute('data-index'));
        this.switchProduct(currentIndex, direction);
      });
    });
  }

  handleAddToCart(itemId, buttonElement) {
    const item = this.findItemById(itemId);
    if (!item) {
      console.error('Item not found:', itemId);
      return;
    }

    console.log('[SidebarIconsLayout] Original item data:', {
      id: item.id,
      name: item.name,
      image: item.image,
      imageUrl: item.imageUrl,
      hasImage: !!(item.image || item.imageUrl)
    });

    const cartItem = {
      id: item.id,
      name: item.name,
      price: item.price?.amount || item.price || 0,
      quantity: 1,
      image: item.image || item.imageUrl,
      currency: item.currency || this.config.settings?.currency || this.config.currency || 'PLN',
      modifiers: {},
      specialInstructions: '',
      totalPrice: item.price?.amount || item.price || 0
    };

    const event = new CustomEvent('itemAddedToCart', {
      detail: cartItem
    });
    document.dispatchEvent(event);

    this.animateAddToCartButton(buttonElement);
  }

  /**
   * Animate add-to-cart button with satisfying feedback
   */
  animateAddToCartButton(buttonElement) {
    buttonElement.innerHTML = '<i class="fas fa-check"></i>';
    buttonElement.style.background = 'var(--color-success, #3E885B)';
    buttonElement.style.color = 'white';
    buttonElement.style.transform = 'scale(1.2)';
    buttonElement.style.transition = 'all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)';

    setTimeout(() => {
      buttonElement.style.transform = 'scale(1.1)';
    }, 150);

    setTimeout(() => {
      buttonElement.innerHTML = '<i class="fas fa-plus"></i>';
      buttonElement.style.background = '';
      buttonElement.style.color = '';
      buttonElement.style.transform = '';
      buttonElement.style.transition = 'all 0.3s ease';
    }, 800);
  }

  findItemById(itemId) {
    for (const categoryItems of Object.values(this.menuData.items)) {
      const item = categoryItems.find(item => item.id === itemId);
      if (item) return item;
    }
    return null;
  }

  show() {
    const container = document.querySelector('.main-container.sidebar-layout');
    if (container) {
      container.style.display = 'flex';
      container.style.opacity = '0';

      requestAnimationFrame(() => {
        container.style.transition = 'opacity 0.4s ease';
        container.style.opacity = '1';

        setTimeout(() => {
          this.loadInitialCategoryWithAnimations();
        }, 500);
      });
    }
  }

  loadInitialCategoryWithAnimations() {
    const menuGrid = document.querySelector('.sidebar-layout .menu-items-grid');
    if (menuGrid) {
      this.animationManager.animateMenuItems(menuGrid, 80);
    }
  }

  hide() {
    return new Promise((resolve) => {
      const container = document.querySelector('.main-container.sidebar-layout');
      if (container) {
        container.style.display = 'none';
      }
      resolve();
    });
  }

  updateCartCount(count) {
    this.cartCount = count;
    const cartCountEl = document.querySelector('.sidebar-layout .cart-count');
    if (cartCountEl) {
      cartCountEl.textContent = count;
    }
  }

  handleLogoClick() {
    const event = new CustomEvent('navigateToWelcome');
    document.dispatchEvent(event);
  }

  getSupportedFeatures() {
    return ['search', 'cart', 'sidebarNavigation', 'categories'];
  }
}
